import { useState, useRef, useEffect, forwardRef } from 'react'
import './NumericInput.css'

/**
 * Drop-in replacement for <input type="number"> that fixes:
 * - Leading zeros persisting (e.g. "088")
 * - Blank fields snapping to 0 instead of staying empty
 *
 * Uses an internal string state while focused so the user has full
 * editing control.  On blur the display is normalised (leading zeros
 * stripped, empty → '').  The parent receives parsed numbers (or ''
 * for empty) via onChange(value).
 */
const NumericInput = forwardRef(function NumericInput(
  { value, onChange, onBlur, onFocus, className = '', ...props },
  forwardedRef,
) {
  const [display, setDisplay] = useState(() => fmt(value))
  const focused = useRef(false)
  const innerRef = useRef(null)
  const ref = forwardedRef || innerRef

  useEffect(() => {
    if (!focused.current) setDisplay(fmt(value))
  }, [value])

  return (
    <input
      {...props}
      ref={ref}
      className={['numeric-input', className].filter(Boolean).join(' ')}
      type="number"
      value={display}
      onFocus={(e) => {
        focused.current = true
        onFocus?.(e)
      }}
      onChange={(e) => {
        const raw = e.target.value
        setDisplay(raw)
        if (raw === '' || raw === '-') {
          onChange?.('')
        } else {
          const n = parseFloat(raw)
          if (!isNaN(n)) onChange?.(n)
        }
      }}
      onBlur={(e) => {
        focused.current = false
        const n = parseFloat(display)
        if (display === '' || isNaN(n)) {
          setDisplay('')
          onChange?.('')
        } else {
          setDisplay(fmt(n))
          onChange?.(n)
        }
        onBlur?.(e)
      }}
    />
  )
})

function fmt(v) {
  if (v === '' || v == null || (typeof v === 'number' && isNaN(v))) return ''
  return String(v)
}

export default NumericInput
